import React, { useMemo, useState } from 'react';
import { useBlockLibrary } from '../../forms/formBuilder/blockLibraryContext';
import { resolveFormBuilderBlockOptions } from '../../forms/formBuilder/optionBlocks';
import { canHideWithOperator } from '../../forms/conditions';
import type {
  BlockCondition,
  BlockConditionAction,
  BlockConditionMode,
  BlockConditionOperator,
  CanvasBlock,
} from '../../forms/types';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';

export interface FormBuilderConditionModalProps {
  block: CanvasBlock;
  blocks: CanvasBlock[];
  open: boolean;
  onClose: () => void;
  /** `alsoApplyTo` — uids of other blocks that get the same rule. */
  onSave: (condition: BlockCondition, alsoApplyTo: string[]) => void;
}

const OPERATORS: BlockConditionOperator[] = [
  'is equal to',
  'is not equal to',
  'is empty',
  'is not empty',
  'contains',
  'does not contain',
  'is greater than',
  'is less than',
];

const ACTION_OPTIONS: { k: BlockConditionAction; l: string }[] = [
  { k: 'show', l: 'Show' },
  { k: 'hide', l: 'Hide' },
  { k: 'require', l: 'Show & require' },
];

export function describeConditionAction(action: BlockConditionAction | undefined): string {
  if (action === 'hide') return 'Hide';
  if (action === 'require') return 'Show & require';
  return 'Show';
}

interface PickOption {
  value: string;
  label: string;
}

interface SourceOption extends PickOption {
  options: string[];
}

export function FormBuilderConditionModal({ block, blocks, open, onClose, onSave }: FormBuilderConditionModalProps) {
  const library = useBlockLibrary();
  const [mode, setMode] = useState<BlockConditionMode>(block.condition?.mode ?? 'simple');
  const [action, setAction] = useState<BlockConditionAction>(block.condition?.action ?? 'show');
  const [sourceUid, setSourceUid] = useState(block.condition?.fieldUid ?? '');
  const [operator, setOperator] = useState<BlockConditionOperator>(block.condition?.operator ?? 'is equal to');
  const [value, setValue] = useState(block.condition?.value ?? '');
  const [alsoApplyTo, setAlsoApplyTo] = useState<string[]>([]);

  // Label each block by its own question text — many blocks share a type ("Radio"),
  // so the library label alone can't tell twenty Yes/No questions apart.
  const blockLabel = useMemo(() => {
    const labelById = new Map(library.map((item) => [item.id, item.label]));
    return (b: CanvasBlock): string =>
      [b.config?.label, b.customLabel, b.title, labelById.get(b.typeId)].find((t) => t?.trim())?.trim() ?? b.typeId;
  }, [library]);

  const numbered = useMemo(
    () => blocks.map((b, index) => ({ b, label: `${String(index + 1)}. ${blockLabel(b)}` })),
    [blocks, blockLabel],
  );

  const sources = useMemo<SourceOption[]>(() => {
    const libraryOptions = new Map(
      library.map((item) => [item.id, item.fields?.length === 1 ? (item.fields[0]?.options ?? []) : []]),
    );
    return numbered
      .filter(({ b }) => b.uid !== block.uid)
      .map(({ b, label }) => ({
        value: b.uid,
        label,
        options: resolveFormBuilderBlockOptions(b.typeId, b.config?.options) ?? libraryOptions.get(b.typeId) ?? [],
      }));
  }, [numbered, block.uid, library]);

  const targets = useMemo(
    () => numbered.filter(({ b }) => b.uid !== block.uid && b.uid !== sourceUid),
    [numbered, block.uid, sourceUid],
  );

  if (!open) return null;

  const source = sources.find((s) => s.value === sourceUid);
  const hideValue = operator === 'is empty' || operator === 'is not empty';
  const operatorChoices = action === 'hide' ? OPERATORS.filter(canHideWithOperator) : OPERATORS;
  const actionText = describeConditionAction(action);
  const canSave = Boolean(source) && (hideValue || value.trim() !== '');

  const handleSave = () => {
    if (!source) return;
    onSave(
      {
        mode,
        action,
        fieldUid: source.value,
        fieldLabel: blockLabel(blocks.find((b) => b.uid === source.value) ?? block),
        operator,
        value: hideValue ? '' : value,
      },
      alsoApplyTo,
    );
  };

  const selectAction = (next: BlockConditionAction) => {
    setAction(next);
    if (next === 'hide' && !canHideWithOperator(operator)) setOperator('is equal to');
  };

  return (
    <div className="fm-condition-modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="fm-condition-modal"
        onClick={(e) => {
          e.stopPropagation();
        }}
        role="dialog"
        aria-labelledby="fm-condition-title"
      >
        <header className="flex items-center gap-3 border-b border-[var(--cream-2)] px-[22px] py-4">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-[9px] bg-[var(--gold-tint)] text-[var(--gold)]">
            <Icon kind="bolt" size={16} />
          </div>
          <div className="min-w-0 flex-1">
            <h2 id="fm-condition-title" className="text-[15px] font-semibold text-[var(--ink-0)]">
              Conditional logic
            </h2>
            <p className="mt-0.5 text-[11.5px] text-[var(--ink-3)]">
              Show, hide or require blocks based on an earlier answer.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex size-[30px] items-center justify-center rounded-lg text-[var(--ink-3)] transition-colors hover:bg-[var(--cream-0)]"
            aria-label="Close"
          >
            <Icon kind="x" size={14} />
          </button>
        </header>

        <div className="cal-scroll flex-1 overflow-y-auto px-[22px] py-5">
          <div className="mb-5 inline-flex gap-0.5 rounded-[9px] border border-[var(--cream-2)] bg-[var(--cream-0)] p-0.5">
            {(
              [
                { k: 'simple' as const, l: 'Simple' },
                { k: 'advanced' as const, l: 'Advanced (AND / OR)' },
              ] as const
            ).map((m) => (
              <button
                key={m.k}
                type="button"
                onClick={() => {
                  setMode(m.k);
                }}
                className={cn(
                  'rounded-[6px] border-0 px-3.5 py-1.5 text-[12px] transition-all',
                  mode === m.k
                    ? 'bg-[var(--cream-1)] font-semibold text-[var(--ink-0)] shadow-[var(--sh-1)]'
                    : 'bg-transparent font-medium text-[var(--ink-3)]',
                )}
              >
                {m.l}
              </button>
            ))}
          </div>

          <FieldRow label="Action">
            <div className="flex flex-wrap gap-1.5">
              {ACTION_OPTIONS.map((opt) => (
                <button
                  key={opt.k}
                  type="button"
                  onClick={() => {
                    selectAction(opt.k);
                  }}
                  className={cn(
                    'rounded-lg border px-3.5 py-2 text-[12.5px] transition-colors',
                    action === opt.k
                      ? 'border-[var(--ai-3)] bg-[var(--ai-tint)] font-semibold text-[var(--ai-3)]'
                      : 'border-[var(--cream-2)] bg-[var(--cream-1)] font-medium text-[var(--ink-1)]',
                  )}
                >
                  {opt.l}
                </button>
              ))}
            </div>
          </FieldRow>

          {mode === 'simple' ? (
            <>
              <div className="mb-4 rounded-xl border border-[color-mix(in_srgb,var(--gold)_30%,var(--cream-2))] bg-[var(--gold-tint)] px-4 py-4">
                <p className="mb-2.5 text-[10.5px] font-semibold uppercase tracking-wide text-[var(--gold)]">
                  Sentence builder
                </p>
                <div className="text-[13px] leading-[1.8] text-[var(--ink-0)]">
                  <span>{actionText} this block</span> <span className="text-[var(--ink-3)]">when</span>{' '}
                  <PickInline
                    value={sourceUid}
                    onChange={(uid) => {
                      setSourceUid(uid);
                      setValue('');
                      setAlsoApplyTo((prev) => prev.filter((id) => id !== uid));
                    }}
                    options={sources}
                    placeholder="select field…"
                  />{' '}
                  <PickInline
                    value={operator}
                    onChange={(v) => {
                      setOperator(v as BlockConditionOperator);
                    }}
                    options={operatorChoices.map((o) => ({ value: o, label: o }))}
                  />{' '}
                  {hideValue ? null : source?.options.length ? (
                    <PickInline
                      value={value}
                      onChange={setValue}
                      options={source.options.map((o) => ({ value: o, label: o }))}
                      placeholder="value…"
                    />
                  ) : (
                    <input
                      value={value}
                      onChange={(e) => {
                        setValue(e.target.value);
                      }}
                      placeholder="value"
                      className="inline-block w-[120px] rounded-md border border-[color-mix(in_srgb,var(--gold)_40%,var(--cream-2))] bg-[var(--cream-1)] px-2 py-1 text-[13px] outline-none focus:border-[var(--ai-3)]"
                    />
                  )}
                </div>
              </div>
              <div className="rounded-lg bg-[var(--cream-0)] px-3 py-2.5 text-[11.5px] leading-relaxed text-[var(--ink-3)]">
                <strong className="text-[var(--ink-1)]">Live preview:</strong>{' '}
                {source ? (
                  <>
                    This block will <strong>{actionText.toLowerCase()}</strong> when &quot;{source.label}&quot;{' '}
                    {operator}
                    {value && !hideValue ? (
                      <>
                        {' '}
                        &quot;<strong>{value}</strong>&quot;
                      </>
                    ) : null}
                    .
                  </>
                ) : (
                  <>Select a source field to build the rule.</>
                )}
              </div>
              {targets.length > 0 ? (
                <div className="mt-4">
                  <FieldRow label="Also apply to">
                    <div className="cal-scroll max-h-40 overflow-y-auto rounded-lg border border-[var(--cream-2)] bg-[var(--cream-1)] p-1">
                      {targets.map(({ b, label }) => {
                        const checked = alsoApplyTo.includes(b.uid);
                        return (
                          <label
                            key={b.uid}
                            className="flex cursor-pointer items-center gap-2 rounded-[5px] px-2 py-1.5 text-[12.5px] text-[var(--ink-1)] hover:bg-[var(--cream-0)]"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => {
                                setAlsoApplyTo((prev) =>
                                  checked ? prev.filter((id) => id !== b.uid) : [...prev, b.uid],
                                );
                              }}
                              className="accent-[var(--ai-3)]"
                            />
                            <span className="truncate">{label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </FieldRow>
                </div>
              ) : null}
            </>
          ) : (
            <div className="rounded-xl border border-[var(--cream-2)] bg-[var(--cream-0)] px-4 py-4">
              <p className="mb-3 text-[12.5px] leading-snug text-[var(--ink-1)]">
                Advanced mode supports nested AND / OR groups. Build complex multi-rule conditions across any
                combination of fields.
              </p>
              <div className="rounded-lg border border-[var(--cream-2)] bg-[var(--cream-1)] px-3 py-2.5 font-[var(--font-mono)] text-[11.5px] text-[var(--ink-3)]">
                Group 1 (ALL) → ⊕ Add rule
                <br />
                <span className="text-[var(--ink-4)]">+ Add OR group</span>
              </div>
            </div>
          )}
        </div>

        <footer className="flex justify-end gap-2.5 border-t border-[var(--cream-2)] bg-[var(--cream-0)] px-[22px] py-3.5">
          <button
            type="button"
            onClick={onClose}
            className="rounded-[9px] border border-[var(--cream-2)] bg-[var(--cream-1)] px-4 py-2 text-[12.5px] font-medium text-[var(--ink-1)]"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canSave}
            onClick={handleSave}
            className={cn(
              'rounded-[9px] border-0 px-4 py-2 text-[12.5px] font-medium text-white',
              canSave ? 'bg-[var(--ai-3)]' : 'cursor-not-allowed bg-[var(--cream-shadow)]',
            )}
          >
            Save condition
          </button>
        </footer>
      </div>
    </div>
  );
}

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-4">
      <p className="mb-1.5 text-[10.5px] font-semibold uppercase tracking-wide text-[var(--ink-3)]">{label}</p>
      {children}
    </div>
  );
}

function PickInline({
  value,
  onChange,
  options,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  options: PickOption[];
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);
  return (
    <span className="relative inline-block">
      <button
        type="button"
        onClick={() => {
          setOpen(!open);
        }}
        className={cn(
          'inline-flex max-w-[260px] items-center gap-1 rounded-md border px-2 py-1 text-[13px] font-medium',
          value
            ? 'border-[color-mix(in_srgb,var(--gold)_40%,var(--cream-2))] bg-[var(--cream-1)] text-[var(--ink-0)]'
            : 'border-[color-mix(in_srgb,var(--gold)_40%,var(--cream-2))] bg-[var(--gold-tint)] text-[var(--ink-3)]',
        )}
      >
        <span className="truncate">{selected?.label ?? [value, placeholder].find(Boolean) ?? 'select…'}</span>
        <Icon kind="down" size={10} />
      </button>
      {open ? (
        <div className="absolute left-0 top-[calc(100%+4px)] z-[100] max-h-60 min-w-[220px] max-w-[360px] overflow-y-auto rounded-[9px] border border-[var(--cream-2)] bg-[var(--cream-1)] p-1 shadow-[var(--sh-2)]">
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => {
                onChange(o.value);
                setOpen(false);
              }}
              className={cn(
                'block w-full truncate rounded-[5px] px-2.5 py-1.5 text-left text-[12.5px] text-[var(--ink-1)] hover:bg-[var(--cream-0)]',
                value === o.value && 'bg-[var(--ai-tint)]',
              )}
            >
              {o.label}
            </button>
          ))}
        </div>
      ) : null}
    </span>
  );
}
